'use client';

import { useEffect } from 'react';

const TAGGED_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid', 'msclkid'];

type WithCookiebot = typeof globalThis & { Cookiebot?: { consent?: { marketing?: boolean } } };

/**
 * Hands the landing URL back to the server the moment marketing consent is granted.
 *
 * The middleware cannot record the arrival that brought someone here: on that request there is no
 * consent yet, and by the time there is, the URL no longer carries the campaign tags. See
 * app/api/attribution/record for the rest of the reasoning.
 *
 * Cookiebot re-fires acceptance on every page load once consent exists, so the guard below limits
 * this to pages that actually look like an arrival. A repeat inside the session window is a no-op
 * server side regardless.
 */
export const AttributionRecovery = () => {
  useEffect(() => {
    const record = () => {
      if ((globalThis as WithCookiebot).Cookiebot?.consent?.marketing !== true) return;

      const params = new URLSearchParams(globalThis.location.search);
      const tagged = TAGGED_PARAMS.some((param) => params.get(param));
      const referrer = globalThis.document.referrer;
      const external = referrer !== '' && parseHost(referrer) !== globalThis.location.host;

      if (!tagged && !external) return;

      // Fire and forget: attribution must never delay or break the page it was triggered from.
      void fetch('/api/attribution/record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referrer }),
        keepalive: true
      }).catch(() => {});
    };

    // Cookiebot loads with beforeInteractive, so acceptance can happen before React hydrates and
    // this listener exists — on a slow connection that is the common case, not the edge case. The
    // consent state is readable synchronously once the script is up, so check it on mount as well.
    // A duplicate is harmless: the same arrival inside the session window is a no-op server side.
    record();

    globalThis.addEventListener('CookiebotOnAccept', record);

    return () => globalThis.removeEventListener('CookiebotOnAccept', record);
  }, []);

  return null;
};

function parseHost(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return undefined;
  }
}
